/**
 * The two scales and the vision cap (ADR-061 decisions 5 and 6, as amended by "the design system lands" and
 * "world zoom 2.5 ships where it fits"): the UI scale, a whole number, and the world zoom, a whole number or
 * 2.5. Every size here is in device pixels, never CSS pixels, because operating-system scaling such as 125
 * percent makes CSS pixels fractional. The client reads these to size its UI and its world camera; the map
 * server reads the cap to bound what it sends each player.
 */

/**
 * A width and a height.
 */
export interface Size {
  readonly width: number;
  readonly height: number;
}

/**
 * What every player sees by default, in tiles, centred on their character: what a 2560 by 1440 screen
 * shows at world zoom 3. Tunable, as the Bible's Appendix A treats such values.
 */
export const VISION_CAP_TILES: Size = Object.freeze({ width: 27, height: 15 });

/**
 * The server's lead outside the vision cap, in tiles on every side: an entity enters a client's interest this far
 * before it reaches the visible rectangle (ADR-034), so a creature reaches a client over the cap plus this margin.
 */
export const VISION_MARGIN_TILES = 3;

/**
 * The logical grid the UI is laid out on, in logical pixels.
 */
export const UI_LOGICAL_SIZE: Size = Object.freeze({ width: 640, height: 360 });

/**
 * The world's tile size in world pixels, which the default zoom fits the cap against.
 */
export const WORLD_TILE_PIXELS = 32;

/**
 * The smallest logical area at which scale 2 is taken where the 640 by 360 grid does not fit twice, in logical
 * pixels (ADR-061's amendment of 2026-10-06; ADR-111 decision 25): the least area the layouts hold, so a window
 * of at least 890 by 460 device pixels is drawn at 2 and a smaller one at 1. The one knob of that rule: at 640 by
 * 360 the rule is the fit alone.
 */
export const UI_SMALLEST_AREA: Size = Object.freeze({ width: 445, height: 230 });

/**
 * The scale a window too small for the grid twice is drawn at while it leaves `UI_SMALLEST_AREA`.
 */
const SMALL_WINDOW_SCALE = 2;

/**
 * The fit: the largest whole number of device pixels per logical pixel at which the 640 by 360 logical grid fits
 * an area, and at least 1. The UI scale of an area that is not a window's own: the render size above render
 * resolution Full, where the small window's lift does not apply (`uiScaleOf`).
 *
 * @param width The area's width in device or render pixels.
 * @param height The area's height in device or render pixels.
 * @returns max(1, floor(min(W / 640, H / 360))): 2 at 1280 by 720, 3 at 1920 by 1080, 4 at 2560 by 1440, 6 at 3840 by 2160.
 */
export function uiFit(width: number, height: number): number {
  return Math.max(1, Math.floor(Math.min(width / UI_LOGICAL_SIZE.width, height / UI_LOGICAL_SIZE.height)));
}

/**
 * The UI scale of a window: the fit (`uiFit`), and 2 where the fit is 1 and the window at 2 still leaves
 * `UI_SMALLEST_AREA`, so a small window is drawn as a 1280 by 720 screen is rather than at half its size.
 *
 * @param deviceWidth The window's width in device pixels.
 * @param deviceHeight The window's height in device pixels.
 * @returns The fit: 2 at 1280 by 720, 3 at 1920 by 1080, 4 at 2560 by 1440, 6 at 3840 by 2160; and 2 for a fit of
 *   1 from 890 by 460: at 1366 by 650, 960 by 1000 and 890 by 460, with 1 at 889 by 460 and 890 by 459.
 */
export function uiScale(deviceWidth: number, deviceHeight: number): number {
  const fit = uiFit(deviceWidth, deviceHeight);
  if (fit >= SMALL_WINDOW_SCALE) {
    return fit;
  }
  const holds = Math.floor(deviceWidth / SMALL_WINDOW_SCALE) >= UI_SMALLEST_AREA.width && Math.floor(deviceHeight / SMALL_WINDOW_SCALE) >= UI_SMALLEST_AREA.height;
  return holds ? SMALL_WINDOW_SCALE : fit;
}

/**
 * The legibility floor of a touch page (ADR-061's amendment of 2026-10-31, decision 5): the least a logical pixel
 * is drawn, in CSS pixels. The knob of `phoneUiScale`.
 */
export const PHONE_MIN_CSS_PER_LOGICAL = 1.25;

/**
 * The UI scale of a touch page: the desktop scale (`uiScale`) wherever it already draws a logical pixel at
 * `PHONE_MIN_CSS_PER_LOGICAL` CSS pixels or more (a tablet), and otherwise the smallest whole number of device
 * pixels that does, ceil(1.25 x ratio).
 *
 * @param deviceWidth The window's width in device pixels.
 * @param deviceHeight The window's height in device pixels.
 * @param ratio Device pixels a CSS pixel; anything that is not a positive number is read as 1.
 * @returns The scale: 4 at 2532 by 1170 and ratio 3 (a 633 by 292 area), 3 at 1334 by 750 and ratio 2, and the
 *   desktop scale, 3, at 2360 by 1640 and ratio 2.
 */
export function phoneUiScale(deviceWidth: number, deviceHeight: number, ratio: number): number {
  const density = Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
  const floor = Math.max(1, Math.ceil(PHONE_MIN_CSS_PER_LOGICAL * density - 1e-9));
  return Math.max(uiScale(deviceWidth, deviceHeight), floor);
}

/**
 * An area the UI is laid out on: a window in device pixels, or, with `aboveFull`, the render size of a window
 * drawn above render resolution Full (ADR-093 decision 10), which is no small window whatever its size. A touch
 * page (`touch`, with the `ratio` it was measured at) takes the phone scale at Full.
 */
export interface UiView extends Size {
  readonly aboveFull?: boolean;
  readonly ratio?: number;
  readonly touch?: boolean;
  readonly step?: number;
}

/**
 * The whole-number steps the viewer's Interface size moves the UI scale by from the automatic one (ADR-061's
 * amendment "interface size"): two smaller, the automatic scale, and two larger.
 */
export const INTERFACE_STEPS = Object.freeze([-2, -1, 0, 1, 2] as const);

/**
 * One of `INTERFACE_STEPS`.
 */
export type InterfaceStep = (typeof INTERFACE_STEPS)[number];

/**
 * The largest whole UI scale an area holds: the largest scale at which the area still leaves `UI_SMALLEST_AREA`
 * logical pixels, the least the HUD's layouts hold, and at least 1.
 *
 * @param width The area's width in device or render pixels.
 * @param height The area's height in device or render pixels.
 * @returns max(1, min(floor(W / 445), floor(H / 230))): 3 at 1600 by 900, 8 at 3840 by 2160, 2 at 1280 by 720.
 */
export function uiScaleMost(width: number, height: number): number {
  return Math.max(1, Math.min(Math.floor(width / UI_SMALLEST_AREA.width), Math.floor(height / UI_SMALLEST_AREA.height)));
}

/**
 * The UI scale of a view, the one rule the overlay, the UI camera and the HUD's layout share: a window's scale
 * (`uiScale`), and above render resolution Full the fit alone of the render size (`uiFit`), so a divisor never draws
 * the HUD larger than Full does.
 *
 * @param view The view; its optional whole `step` moves the scale from the automatic one, never below 1 and never past
 *   what the area holds (`uiScaleMost`), nor below the automatic scale when that already is past it.
 * @returns The scale: 2 for a 1280 by 690 window, 1 for the 1280 by 690 render size of a 2560 by 1380 window
 *   at Half, and 4 for a touch view of 2532 by 1170 at ratio 3 (`phoneUiScale`).
 */
export function uiScaleOf(view: UiView): number {
  const base = autoUiScale(view);
  const step = view.step === undefined || !Number.isFinite(view.step) ? 0 : Math.round(view.step);
  return step === 0 ? base : Math.max(1, Math.min(base + step, Math.max(base, uiScaleMost(view.width, view.height))));
}

/**
 * The automatic UI scale of a view, before the viewer's Interface size.
 *
 * @param view The view.
 * @returns The scale `uiScaleOf` gives a view with no `step`.
 */
function autoUiScale(view: UiView): number {
  if (view.aboveFull === true) {
    return uiFit(view.width, view.height);
  }
  return view.touch === true && view.ratio !== undefined ? phoneUiScale(view.width, view.height, view.ratio) : uiScale(view.width, view.height);
}

/**
 * The world zooms between two whole numbers that may be drawn: 2.5 only, the zoom P1.9-T5 measured, drawn at 3
 * and box-resampled (CLIENT.md 7.6). 1.5 and 4.5 wait for measurements of their own.
 */
export const IN_BETWEEN_WORLD_ZOOMS: readonly number[] = Object.freeze([2.5]);

/**
 * Whether the world may be drawn at a zoom: a whole number of at least 1, or one of `IN_BETWEEN_WORLD_ZOOMS`.
 *
 * @param zoom The zoom.
 * @returns True for 1, 2, 2.5, 3, 4 and every whole number above.
 */
export function isAllowedWorldZoom(zoom: number): boolean {
  return (Number.isInteger(zoom) && zoom >= 1) || IN_BETWEEN_WORLD_ZOOMS.includes(zoom);
}

/**
 * The default world zoom: the smallest allowed zoom at or above the exact fit of the vision cap, and at least 1,
 * so the view fits inside the cap. The exact fit is the zoom at which the view is the cap on its binding axis,
 * max(W / (32 x 27), H / (32 x 15)); the allowed zooms are the whole numbers and 2.5.
 *
 * @param deviceWidth The canvas's width in device pixels.
 * @param deviceHeight The canvas's height in device pixels.
 * @returns 2 at 1280 by 720 and 1366 by 768, 2.5 at 1920 by 1080 and 1920 by 1200 (windowed or not), 3 at 2560 by
 * 1440, 5 at 3840 by 2160.
 */
export function defaultWorldZoom(deviceWidth: number, deviceHeight: number): number {
  const across = deviceWidth / (WORLD_TILE_PIXELS * VISION_CAP_TILES.width);
  const down = deviceHeight / (WORLD_TILE_PIXELS * VISION_CAP_TILES.height);
  const fit = Math.max(across, down);
  let zoom = Math.max(1, Math.ceil(fit));
  for (const between of IN_BETWEEN_WORLD_ZOOMS) {
    if (between >= fit && between < zoom) {
      zoom = between;
    }
  }
  return zoom;
}

/**
 * The world zoom some steps from a default through the zooms allowed where it is the default: the whole numbers,
 * and the default itself when it lies between two, so 2.5 is reached only from where it is the default. The
 * viewer's Farther, Default and Closer (-1, 0 and 1) are steps from the window's default; Farther shows more fog
 * past the vision cap, never more world (ADR-061 decision 6).
 *
 * @param defaultZoom The window's default zoom, as `defaultWorldZoom` gives it.
 * @param step Places to move: negative is farther, positive closer.
 * @returns The zoom, at least 1: from 2.5, one step gives 2 or 3; from a whole default, the whole zoom `step` away.
 */
export function stepWorldZoom(defaultZoom: number, step: number): number {
  if (step > 0) {
    return Math.floor(defaultZoom) + step;
  }
  if (step < 0) {
    return Math.max(1, Math.ceil(defaultZoom) + step);
  }
  return defaultZoom;
}

/**
 * The largest default zoom `widestLoadedView` considers; past it a Farther step widens the view by at most 8/7
 * of the cap, less than at a default of 2.
 */
const WIDEST_DEFAULT_CHECKED = 8;

/**
 * The widest world view, in world pixels, any screen shows at any zoom a player can choose, the view the game's
 * chunk painter loads chunks for (ADR-088 decision 2). A screen whose default zoom is `z` is at most `z` times the
 * cap on each axis, and Farther shows it at `stepWorldZoom(z, -1)`, so its view is at most the cap times their
 * ratio; the widest is the largest ratio over the allowed defaults, 2 (a default of 2 stepped to 1), 1728 by 960.
 *
 * @returns The view in world pixels.
 */
export function widestLoadedView(): Size {
  const defaults = [...IN_BETWEEN_WORLD_ZOOMS, ...Array.from({ length: WIDEST_DEFAULT_CHECKED }, (_, i) => i + 1)];
  let ratio = 1;
  for (const zoom of defaults) {
    ratio = Math.max(ratio, zoom / stepWorldZoom(zoom, -1));
  }
  return { width: VISION_CAP_TILES.width * WORLD_TILE_PIXELS * ratio, height: VISION_CAP_TILES.height * WORLD_TILE_PIXELS * ratio };
}

/**
 * A CSS size in device pixels, for a browser that does not report an element's device-pixel box.
 *
 * @param cssWidth The width in CSS pixels, such as `innerWidth`.
 * @param cssHeight The height in CSS pixels, such as `innerHeight`.
 * @param ratio Device pixels per CSS pixel (`devicePixelRatio`).
 * @returns Each side times the ratio, rounded to a whole device pixel.
 */
export function deviceSize(cssWidth: number, cssHeight: number, ratio: number): Size {
  return { width: Math.round(cssWidth * ratio), height: Math.round(cssHeight * ratio) };
}
